/**
 * Core Layer - Raw Environment Variables
 *
 * IMPORTANT: Next.js requires literal `process.env.VARIABLE_NAME` syntax
 * for static analysis. Dynamic access like `process.env[key]` won't work.
 *
 * This file provides type-safe access to raw environment variables.
 * Parsing and validation is handled by env-utils.ts and site.ts
 */

// ============================================================================
// RAW ENV VALUES
// Direct process.env access for Next.js static analysis compatibility
// ============================================================================

export const rawEnv = {
  // App Configuration
  appName: process.env.NEXT_PUBLIC_APP_NAME,
  appDescription: process.env.NEXT_PUBLIC_APP_DESCRIPTION,
  appUrl: process.env.NEXT_PUBLIC_APP_URL,
  appVersion: process.env.NEXT_PUBLIC_APP_VERSION,

  // API Configuration
  apiKey: process.env.NEXT_PUBLIC_API_KEY,
  apiBaseUrl: process.env.NEXT_PUBLIC_API_BASE_URL,
  useLocalBackend: process.env.NEXT_PUBLIC_USE_LOCAL_BACKEND,
  localBackendUrl: process.env.NEXT_PUBLIC_LOCAL_BACKEND_URL,
  productionBackendUrl: process.env.NEXT_PUBLIC_PRODUCTION_BACKEND_URL,
  apiVersion: process.env.NEXT_PUBLIC_API_VERSION,
  apiMainPath: process.env.NEXT_PUBLIC_API_MAIN_PATH,
  apiTimeout: process.env.NEXT_PUBLIC_API_TIMEOUT,
  defaultEndpoint: process.env.NEXT_PUBLIC_DEFAULT_ENDPOINT,

  // Security
  encryptionKey: process.env.NEXT_PUBLIC_ENCRYPTION_KEY,

  // reCAPTCHA
  recaptchaSiteKey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,

  // Contact Information
  contactOfficeAddress: process.env.NEXT_PUBLIC_CONTACT_OFFICE_ADDRESS,
  contactWorkingHours: process.env.NEXT_PUBLIC_CONTACT_WORKING_HOURS,
  contactPhone: process.env.NEXT_PUBLIC_CONTACT_PHONE,
  contactEmail: process.env.NEXT_PUBLIC_CONTACT_EMAIL,
  contactLinkedIn: process.env.NEXT_PUBLIC_CONTACT_LINKEDIN,

  // Localization
  defaultLanguage: process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE,
  supportedLocales: process.env.NEXT_PUBLIC_SUPPORTED_LOCALES,
  defaultTimezone: process.env.NEXT_PUBLIC_DEFAULT_TIMEZONE,

  // Theme
  defaultTheme: process.env.NEXT_PUBLIC_DEFAULT_THEME,

  // Pagination
  defaultPageSize: process.env.NEXT_PUBLIC_DEFAULT_PAGE_SIZE,
  maxPageSize: process.env.NEXT_PUBLIC_MAX_PAGE_SIZE,

  // File Upload
  maxFileSize: process.env.NEXT_PUBLIC_MAX_FILE_SIZE,
  allowedFileTypes: process.env.NEXT_PUBLIC_ALLOWED_FILE_TYPES,

  // Cache
  cacheTTL: process.env.NEXT_PUBLIC_CACHE_TTL,
  enableCache: process.env.NEXT_PUBLIC_ENABLE_CACHE,

  // Feature Flags
  enableAnalytics: process.env.NEXT_PUBLIC_ENABLE_ANALYTICS,
  enableErrorReporting: process.env.NEXT_PUBLIC_ENABLE_ERROR_REPORTING,
  enableLogging: process.env.NEXT_PUBLIC_ENABLE_LOGGING,

  // Google Maps
  googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
  googleMapsId: process.env.NEXT_PUBLIC_GOOGLE_MAPS_ID,

  // Environment (NODE_ENV is always available)
  nodeEnv: process.env.NODE_ENV,
} as const;

/**
 * Type for raw environment values
 */
export type RawEnv = typeof rawEnv;

/**
 * Environment mode helpers
 */
export const isDevelopment = rawEnv.nodeEnv === "development";
export const isProduction = rawEnv.nodeEnv === "production";
export const isTest = rawEnv.nodeEnv === "test";
